/**
 * Custom element for a "Load More" button.
 * Fetches and appends the next page of products when clicked or when the button becomes visible.
 */

class LoadMoreButton extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false; // Add a loading flag
  }
  /**
   * Called when the element is connected to the DOM.
   * Sets up event listeners and an IntersectionObserver for the "Load More" button.
   */
  connectedCallback() {
    this.loadMoreBtn = this.querySelector('button');
    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener("click", this.fetchNextPage.bind(this));

      // Observer for visibility detection
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.fetchNextPage();
            observer.unobserve(this.loadMoreBtn);
            observer.observe(this.loadMoreBtn);
          }
        });
      });

      observer.observe(this.loadMoreBtn);
    }
  }
 /**
   * Fetches the next page of products and appends them to the product grid.
   * Updates the "data-next-page" attribute and hides the button if no more pages are available.
   */
  fetchNextPage() {
    if (this.isLoading) return; // Prevent duplicate fetches while loading
    this.isLoading = true; // Set loading flag

    let nextPageUrl = document
      .querySelector(".collection-product-container")
      .getAttribute("data-next-page");

    if (!nextPageUrl) {
      this.loadMoreBtn.style.display = "none"; // Hide the next button if there's no next page
      this.isLoading = false; // Reset loading flag
      return;
    }

    // Fetch next page
    fetch(nextPageUrl)
      .then((response) => response.text())
      .then((html) => {
        const updatedHTML = new DOMParser().parseFromString(html, "text/html");
        const newProducts = updatedHTML.querySelector(".collection-product-container");

        const newUrl = window.location.origin + nextPageUrl;
        window.history.pushState({ path: newUrl }, "", newUrl);

        // Use a new variable to avoid overwriting nextPageUrl mid-way
        const updatedNextPage = newProducts.getAttribute("data-next-page");
        const prevPageUrl = newProducts.getAttribute("data-prev-page");

        const productGrid = document.querySelector(".collection-product-container");

        // Preserve scroll position before appending new products
        const firstNewItem = newProducts.firstElementChild;
        const offsetTopBefore = firstNewItem.getBoundingClientRect().top;
        productGrid.insertAdjacentHTML("beforeend", newProducts.innerHTML);
        theme.reinitProductGridItem();


        // Preserve scroll position after appending new products
        const offsetTopAfter = firstNewItem.getBoundingClientRect().top;
        window.scrollBy(0, offsetTopAfter - offsetTopBefore);

        if (updatedNextPage) {
          productGrid.setAttribute("data-next-page", updatedNextPage);
          productGrid.setAttribute("data-prev-page", prevPageUrl);
        } else {
          productGrid.removeAttribute("data-next-page");
          this.loadMoreBtn.style.display = "none"; // Hide the button if no more pages
        }
      })
      .catch((error) => {
        console.error("Error fetching the next page:", error);
      })
      .finally(() => {
        this.isLoading = false; // Reset loading flag after the fetch completes
      });
  }
}

/**
 * Custom element for a "Load Previous" button.
 * Fetches and prepends the previous page of products when clicked or when the button becomes visible.
 */
class LoadPrevButton extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false; // Initialize loading flag
  }

    /**
   * Called when the element is connected to the DOM.
   * Sets up event listeners and an IntersectionObserver for the "Load Previous" button.
   */

  connectedCallback() {
    this.loadPrevBtn = this.querySelector('button');
    if (this.loadPrevBtn) {
      this.loadPrevBtn.addEventListener("click", this.fetchPrevPage.bind(this));

      // Observer for visibility detection
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          // Trigger fetch when button is visible
          if (entry.isIntersecting ) {
            this.fetchPrevPage();
            observer.unobserve(this.loadPrevBtn);
            observer.observe(this.loadPrevBtn);
            
          }
        });
      });

      observer.observe(this.loadPrevBtn);
    }
  }
  /**
   * Fetches the previous page of products and prepends them to the product grid.
   * Updates the "data-prev-page" attribute and hides the button if no more pages are available.
   */
  fetchPrevPage() {
    if (this.isLoading) {
      return; // Prevent duplicate fetches
    }
    this.isLoading = true; // Set loading flag to true during the fetch

    let prevPageUrl = document
      .querySelector(".collection-product-container")
      .getAttribute("data-prev-page");

    if (!prevPageUrl) {
      this.loadPrevBtn.style.display = "none"; // Hide prev button if no previous page
      this.isLoading = false; // Reset loading flag
      return;
    }


    fetch(prevPageUrl)
      .then((response) => response.text())
      .then((html) => {
        const updatedHTML = new DOMParser().parseFromString(html, "text/html");
        const newProducts = updatedHTML.querySelector(".collection-product-container");

        const updatedPrevPage = newProducts.getAttribute("data-prev-page");
        const productGrid = document.querySelector(".collection-product-container");

        // Preserve scroll position before prepending new products
        const firstNewItem = newProducts.firstElementChild;
        const offsetTopBefore = firstNewItem.getBoundingClientRect().top;

        // Insert new content at the top
        // const images =  newProducts.querySelectorAll('.image-element');
        // images.forEach((image) => {
        // image.style.opacity = '1';
        // });
        productGrid.insertAdjacentHTML("afterbegin", newProducts.innerHTML);
        // theme.initQuickShop();
            
            
       theme.reinitProductGridItem();

        // Preserve scroll position after prepending new products
        const offsetTopAfter = firstNewItem.getBoundingClientRect().top;
        window.scrollBy(0, offsetTopAfter - offsetTopBefore);

        if (updatedPrevPage) {
          productGrid.setAttribute("data-prev-page", updatedPrevPage);
          this.loadPrevBtn.style.display = "flex"; // Show prev button if there are more pages
        } else {
          productGrid.removeAttribute("data-prev-page");
          this.loadPrevBtn.style.display = "none"; // Hide prev button if no more previous pages
        }

      })
      .catch((error) => {
        console.error("Error fetching the previous page:", error);
      })
      .finally(() => {
        this.isLoading = false; // Reset loading flag after the fetch is complete
      });
  }
}




// Define custom elements
customElements.define('load-more', LoadMoreButton);
customElements.define('load-prev', LoadPrevButton);
